/**
 * Input Security — scans free-text values for embedded code.
 * Covers: XSS, HTML injection, SQL injection, template injection, command injection.
 * Apply to every <input type="text"> and <textarea> before storing or submitting.
 */

const INJECTION_PATTERNS = [
  // XSS — script tags
  { re: /<\s*script[\s>/]/i,          label: 'script tag' },
  { re: /<\/\s*script\s*>/i,          label: 'script tag' },
  // XSS — inline event handlers  (onclick=, onerror=, onload=, …)
  { re: /\bon\w+\s*=/i,               label: 'event handler attribute' },
  // XSS — dangerous protocols
  { re: /javascript\s*:/i,            label: 'javascript: protocol' },
  { re: /vbscript\s*:/i,              label: 'vbscript: protocol' },
  { re: /data\s*:\s*text\/html/i,     label: 'data: URI' },
  // HTML injection — embedding frames / objects
  { re: /<\s*(iframe|frame|object|embed|applet|base|meta|link)\b/i, label: 'embedded tag' },
  // HTML injection — generic open tag with attributes that could carry code
  { re: /<\s*\w+[^>]*\s(on\w+|href\s*=\s*["']?javascript)/i, label: 'HTML with code attribute' },
  // SQL injection — quote-based boolean bypass
  { re: /('|")\s*(OR|AND)\s*('|"|\d)/i,  label: 'SQL boolean injection' },
  // SQL injection — stacked / destructive statements
  { re: /;\s*(DROP|DELETE|TRUNCATE|INSERT|UPDATE|ALTER|CREATE)\s+/i, label: 'SQL statement' },
  // SQL injection — UNION SELECT
  { re: /\bUNION\s+(ALL\s+)?SELECT\b/i,  label: 'SQL UNION' },
  // SQL injection — stored procedure execution
  { re: /\b(EXEC|EXECUTE)\s*\(/i,         label: 'SQL EXEC' },
  // Template injection — Handlebars / Angular / Jinja / Twig / EJS
  { re: /\{\{[\s\S]*?\}\}/,              label: 'template expression {{ }}' },
  { re: /\{%[\s\S]*?%\}/,               label: 'template tag {% %}' },
  { re: /<%([\s\S]*?)%>/,               label: 'template tag <% %>' },
  // Command injection — shell backtick & $() substitution
  { re: /`[^`]+`/,                       label: 'shell command substitution' },
  { re: /\$\([^)]+\)/,                   label: 'command substitution $()' },
]

/**
 * Scans a string for embedded code patterns.
 * @param {string} value
 * @returns {string} Error message if unsafe, empty string if safe.
 */
export function scanInput(value) {
  if (!value || typeof value !== 'string') return ''
  for (const { re, label } of INJECTION_PATTERNS) {
    if (re.test(value)) {
      return `Input contains potentially unsafe content (${label}). Please remove any embedded code or markup.`
    }
  }
  return ''
}

/** Returns true when the value contains no code injection. */
export const isSafeInput = (value) => scanInput(value) === ''
